import React, { useState, useEffect, useRef, useCallback, lazy, Suspense, memo } from 'react';
import { SpeechToTextService } from './services/speechToText';
import { TranslationService } from './services/translationService';
import { SUPPORTED_LANGUAGES, BEDROCK_MODELS, DEFAULT_MODEL, AWS_CONFIG } from './utils/awsConfig';
import AuthWrapper, { useCredentials, useSettings } from './components/AuthWrapper';
import SystemPromptEditor from './components/SystemPromptEditor';
import { S3Client, PutObjectCommand, GetObjectCommand, CreateMultipartUploadCommand, UploadPartCommand, CompleteMultipartUploadCommand, AbortMultipartUploadCommand } from '@aws-sdk/client-s3';
import { TranscribeClient, StartTranscriptionJobCommand, GetTranscriptionJobCommand } from '@aws-sdk/client-transcribe';
import './aws-config'; // Initialize Amplify
import './App.css';

// Lazy load heavy components
const TokenUsageSection = lazy(() => import('./components/TokenUsageSection'));
const TranslationHistory = lazy(() => import('./components/TranslationHistory'));

// Memoized components for better performance
const StatusDisplay = memo(({ status, isRecording }) => {
  const [visible, setVisible] = useState(false);
  const [currentStatus, setCurrentStatus] = useState('');
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (status !== currentStatus) {
      setCurrentStatus(status);
      setVisible(true);
      
      // Clear existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      // Hide after 5 seconds unless recording
      if (!isRecording) {
        timeoutRef.current = setTimeout(() => {
          setVisible(false);
        }, 5000);
      }
    }
  }, [status, currentStatus, isRecording]);

  useEffect(() => {
    // Keep visible while recording
    if (isRecording) {
      setVisible(true);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    }
  }, [isRecording]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  if (!visible) return null;

  const getStatusEmoji = () => {
    if (isRecording) return '🔴';
    if (status.includes('Ready')) return '✅';
    if (status.includes('failed') || status.includes('error')) return '❌';
    if (status.includes('Testing') || status.includes('Initializing')) return '⏳';
    if (status.includes('Translation')) return '🔄';
    return 'ℹ️';
  };

  return (
    <>
      <style>{`
        @keyframes slideInUp {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
      <div style={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        backgroundColor: isRecording ? '#dc3545' : 
                       status.includes('Ready') ? '#28a745' : 
                       status.includes('failed') || status.includes('error') ? '#dc3545' : '#007bff',
        color: 'white',
        padding: '10px 16px',
        borderRadius: '8px',
        fontSize: '13px',
        fontWeight: '500',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        animation: 'slideInUp 0.3s ease-out'
      }}>
        <span>{getStatusEmoji()}</span>
        {status}
        {isRecording && <span style={{ fontSize: '10px', opacity: 0.8 }}>LIVE</span>}
      </div>
    </>
  );
});

function AppContent() {
  const credentials = useCredentials();
  const { sourceLanguage, targetLanguage, selectedModel, customSystemPrompt, setSourceLanguage, setTargetLanguage, realTimeMode, setRealTimeMode, showReasoning, setShowReasoning } = useSettings();
  
  // Application state
  const [isRecording, setIsRecording] = useState(false);
  const [transcribedText, setTranscribedText] = useState('');
  const [translatedText, setTranslatedText] = useState('');
  const [originalTranslatedText, setOriginalTranslatedText] = useState(''); // Store original with reasoning

  // Helper function to update translation text
  const updateTranslatedText = useCallback((newText) => {
    setOriginalTranslatedText(newText); // Always store the original
    
    // Apply reasoning filter if needed
    if (!showReasoning && newText.includes('<reasoning>') && newText.includes('</reasoning>')) {
      const reasoningEnd = newText.indexOf('</reasoning>');
      let cleanText = newText.substring(reasoningEnd + 12).trim();
      if (cleanText.startsWith('"') && cleanText.endsWith('"')) {
        cleanText = cleanText.slice(1, -1);
      }
      setTranslatedText(cleanText);
    } else {
      setTranslatedText(newText);
    }
  }, [showReasoning]);
  const [status, setStatus] = useState('Initializing...');
  const [error, setError] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);
  const [manualText, setManualText] = useState('');
  const [tokenUsage, setTokenUsage] = useState(null);
  // Add viewport meta tag to prevent zoom on iOS
  useEffect(() => {
    // Check if we're on iOS Safari
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
    
    if (isIOS || isSafari) {
      // Update viewport meta tag to prevent zoom
      let viewport = document.querySelector('meta[name="viewport"]');
      if (!viewport) {
        viewport = document.createElement('meta');
        viewport.name = 'viewport';
        document.head.appendChild(viewport);
      }
      viewport.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
      
      console.log('🔵 iOS Safari detected - zoom prevention enabled');
    }
  }, []);

  // State variables for language dropdowns
  const [sourceLanguageOpen, setSourceLanguageOpen] = useState(false);
  const [targetLanguageOpen, setTargetLanguageOpen] = useState(false);

  // Add click outside handlers for language dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sourceLanguageOpen && !event.target.closest('[data-source-lang-dropdown]')) {
        setSourceLanguageOpen(false);
      }
      if (targetLanguageOpen && !event.target.closest('[data-target-lang-dropdown]')) {
        setTargetLanguageOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [sourceLanguageOpen, targetLanguageOpen]);

  const handleFileRemoval = () => {
    setSelectedAudioFile(null);
    setIsProcessing(false);
    setSourceLanguage('en'); // Reset to English when file removed
    // Reset file input to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const [translationHistory, setTranslationHistory] = useState([]);
  const [selectedAudioFile, setSelectedAudioFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false); // Track if any mode is processing
  const [isProcessingAudio, setIsProcessingAudio] = useState(false);
  const fileInputRef = useRef(null); // Reference to file input
  const [activeTab, setActiveTab] = useState('recording'); // Mobile tab state
  const [availableMicrophones, setAvailableMicrophones] = useState([]);
  const [selectedMicrophone, setSelectedMicrophone] = useState('default');
  const [uploadProgress, setUploadProgress] = useState(0);

  // Service references
  const speechService = useRef(null);
  const translationService = useRef(null);
  const translationTimeout = useRef(null);

  // Translate text using AWS Bedrock
  const translateText = useCallback(async (text, isRealTime = false) => {
    if (!text.trim() || !translationService.current) return;

    try {
      setStatus(isRealTime ? 'Translating (real-time)...' : 'Translating...');
      
      if (!isRealTime && translationTimeout.current) {
        clearTimeout(translationTimeout.current);
      }

      const translateFunction = async () => {
        try {
          const translation = await translationService.current.translateText(
            text.trim(),
            sourceLanguage,
            targetLanguage,
            isRealTime
          );
          
          updateTranslatedText(translation);
          setStatus(isRealTime ? 'Real-time translation' : 'Translation complete');
          setError('');

          const usage = translationService.current.getTokenUsage();
          setTokenUsage(usage);

          if (!isRealTime) {
            setTranslationHistory(prev => [...prev, {
              id: Date.now(),
              timestamp: new Date(),
              source: text.trim(),
              translation,
              sourceLanguage,
              targetLanguage,
              model: selectedModel,
              customPrompt: !!customSystemPrompt
            }].slice(-20));
          }

        } catch (err) {
          console.error('Translation error:', err);
          
          if (err.message.includes('credentials') || err.message.includes('expired')) {
            setError('AWS credentials issue. Please check your AWS configuration.');
          } else {
            setError(`Translation error: ${err.message}`);
            setStatus('Translation failed');
          }
        }
      };

      if (isRealTime) {
        if (translationTimeout.current) {
          clearTimeout(translationTimeout.current);
        }
        translationTimeout.current = setTimeout(translateFunction, 300);
      } else {
        translationTimeout.current = setTimeout(translateFunction, 500);
      }
      
    } catch (err) {
      console.error('Translation setup error:', err);
      setError(`Translation error: ${err.message}`);
      setStatus('Translation failed');
    }
  }, [sourceLanguage, targetLanguage, selectedModel, customSystemPrompt]);

  // Handle speech recognition results
  const handleSpeechResult = useCallback(async (result) => {
    console.log('🔵 Speech result received:', result);
    
    // Add proper spacing between sentences
    const finalText = result.final ? result.final.trim() : '';
    const interimText = result.interim ? result.interim.trim() : '';
    
    let fullText = '';
    if (finalText && interimText) {
      fullText = finalText + ' ' + interimText;
    } else {
      fullText = finalText + interimText;
    }
    
    console.log('🔵 Full text:', fullText);
    
    setTranscribedText(fullText);

    if (result.languageChanged) {
      setError('Switched to English for better speech recognition compatibility');
      setSourceLanguage('en');
    }

    // Only translate in real-time mode
    if (realTimeMode) {
      if (result.interim.trim().length > 0) {
        console.log('🔵 Real-time translation for interim:', result.interim);
        await translateText(fullText, true);
      }

      if (result.isFinal && result.final.trim().length > 0) {
        console.log('🔵 Final translation for:', result.final);
        await translateText(result.final, false);
      }
    }
    // In non-real-time mode, don't translate during recording
  }, [translateText, realTimeMode]);

  // Handle speech recognition errors
  const handleSpeechError = useCallback((error) => {
    console.error('Speech recognition error:', error);
    setError(`Speech recognition error: ${error.message}`);
    setIsRecording(false);
    setStatus('Speech recognition failed');
  }, []);

  // Initialize services
  useEffect(() => {
    const initializeServices = async () => {
      try {
        setStatus('Initializing services...');
        setError('');
        console.log('🔵 Starting service initialization...');
        console.log('🔵 Credentials available:', !!credentials);
        
        // Wait a bit for credentials to be fully available
        if (credentials) {
          console.log('🔵 Waiting for credentials to stabilize...');
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
        
        console.log('🔵 Initializing speech recognition...');
        speechService.current = new SpeechToTextService();
        
        // Don't request microphone permission during initialization
        // Will be requested only when user clicks "Start Recording"
        
        const speechTest = SpeechToTextService.testSpeechRecognition();
        console.log('🔵 Speech test result:', speechTest);
        
        if (!speechTest.supported) {
          throw new Error('Speech recognition is not supported in this browser. Please use Chrome, Edge, or Safari.');
        }
        
        speechService.current.initialize(
          sourceLanguage,
          handleSpeechResult,
          handleSpeechError
        );
        console.log('🟢 Speech recognition initialized successfully');

        console.log('🔵 Creating TranslationService...');
        console.log('🔵 Selected model:', selectedModel);
        console.log('🔵 Credentials details:', credentials ? 'Available' : 'Not available');
        
        // Preserve existing token usage if service already exists
        const existingTokenUsage = translationService.current ? translationService.current.tokenUsage : null;
        
        // FIXED: Use original constructor with preserved token usage
        translationService.current = new TranslationService(selectedModel, credentials, existingTokenUsage);
        
        // Set custom system prompt and reasoning preference after creation
        if (customSystemPrompt) {
          // Process placeholders in the custom prompt
          const processedPrompt = customSystemPrompt
            .replace(/\{SOURCE_LANGUAGE\}/g, SUPPORTED_LANGUAGES[sourceLanguage]?.name || 'English')
            .replace(/\{TARGET_LANGUAGE\}/g, SUPPORTED_LANGUAGES[targetLanguage]?.name || 'English');
          translationService.current.updateSystemPrompt(processedPrompt);
        }
        translationService.current.setShowReasoning(showReasoning);
        
        setStatus('Testing AWS Bedrock connection...');
        console.log('🔵 Testing Bedrock connection...');
        
        const isConnected = await translationService.current.testConnection();
        if (!isConnected) {
          throw new Error('Failed to connect to AWS Bedrock. Please check your AWS credentials and permissions.');
        }

        console.log('🟢 Bedrock connection successful');
        console.log('🟢 All services initialized successfully');
        setIsInitialized(true);
        setStatus('Ready to start recording');
        setError('');
        
      } catch (err) {
        console.error('🔴 Service initialization error:', err);
        setError(err.message);
        setStatus('Initialization failed');
      }
    };

    initializeServices();

    return () => {
      if (speechService.current) {
        speechService.current.cleanup();
      }
      if (translationTimeout.current) {
        clearTimeout(translationTimeout.current);
      }
    };
  }, [sourceLanguage, selectedModel, credentials, customSystemPrompt, handleSpeechResult, handleSpeechError]);

  // Control functions
  const startRecording = useCallback(async () => {
    console.log('🔵 Start recording button clicked');
    
    if (!isInitialized) {
      console.log('🔴 Services not initialized yet');
      setError('Services not initialized yet');
      return;
    }

    if (!speechService.current) {
      console.log('🔴 Speech service not available');
      setError('Speech recognition service not available');
      return;
    }

    // Request microphone permission and get available devices only when user starts recording
    try {
      if (availableMicrophones.length === 0) {
        console.log('🔵 Getting available microphones...');
        const mics = await speechService.current.getAvailableMicrophones();
        setAvailableMicrophones(mics);
        console.log('🔵 Available microphones loaded:', mics.length);
      }
    } catch (micError) {
      console.log('🔵 Could not enumerate microphones:', micError.message);
      // Continue anyway, will use default microphone
    }

    try {
      console.log('🔵 Starting recording process...');
      setIsProcessing(true);
      setError('');
      setStatus('🎤 Requesting microphone access...');
      
      setTranscribedText('');
      setTranslatedText('');
      setOriginalTranslatedText('');
      
      const testResult = speechService.current.constructor.testSpeechRecognition();
      console.log('🔵 Speech test result:', testResult);
      
      if (!testResult.supported) {
        throw new Error('Speech recognition is not supported in this browser. Please use Chrome, Edge, or Safari.');
      }
      
      setStatus('Starting recording...');
      
      console.log('🔵 Starting speech service...');
      await speechService.current.start();
      setIsRecording(true);
      setStatus('Recording... Speak now!');
      console.log('🟢 Recording started successfully');
      
    } catch (err) {
      console.error('🔴 Start recording error:', err);
      setError(err.message);
      setStatus('❌ Failed to start recording');
      setIsRecording(false);
      setIsProcessing(false);
    }
  }, [isInitialized]);

  const stopRecording = useCallback(() => {
    console.log('🔵 Stop recording button clicked');
    if (speechService.current && isRecording) {
      console.log('🔵 Stopping speech service...');
      speechService.current.stop();
      setIsRecording(false);
      setIsProcessing(false);
      setStatus('⏹️ Recording stopped');
      console.log('🟢 Recording stopped successfully');
      
      // Translate accumulated text when recording stops (non-real-time mode)
      if (!realTimeMode && transcribedText.trim()) {
        console.log('🔵 Non-real-time mode: translating accumulated text');
        translateText(transcribedText, false);
      }
    }
  }, [isRecording, realTimeMode, transcribedText, translateText]);

  const clearText = useCallback(() => {
    setTranscribedText('');
    setTranslatedText('');
    setOriginalTranslatedText('');
    setError('');
    setStatus('Ready');
    setManualText('');
  }, []);

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setIsProcessing(true);
    
    // Validate file size (max 2GB for Transcribe)
    if (file.size > 2 * 1024 * 1024 * 1024) {
      setError('File size must be less than 2GB for transcription');
      setIsProcessing(false);
      return;
    }
    
    // Validate file type
    const validTypes = ['.amr', '.flac', '.m4a', '.mp3', '.mp4', '.ogg', '.webm', '.wav'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!validTypes.includes(fileExtension)) {
      setError(`Unsupported file type: ${fileExtension}. Supported formats: ${validTypes.join(', ')}`);
      setIsProcessing(false);
      return;
    }
    
    setError(''); // Clear any previous errors
    setSelectedAudioFile(file);
    
    // Auto-switch to auto-detect when file is selected
    setSourceLanguage('auto');
  };

  // Handle audio file upload and transcription
  const handleAudioFileUpload = async () => {
    if (!selectedAudioFile) return;

    setIsProcessingAudio(true);
    setStatus('📁 Preparing upload...');

    // Auto-switch to auto-detect for file uploads
    if (sourceLanguage !== 'auto') {
      setSourceLanguage('auto');
    }

    try {
      // Check credentials first
      if (!credentials) {
        throw new Error('No AWS credentials available');
      }

      console.log('Credentials available:', !!credentials);
      console.log('Region:', AWS_CONFIG.region);

      // Get current user for S3 path
      const { getCurrentUser } = await import('aws-amplify/auth');
      const user = await getCurrentUser();
      const now = new Date();
      const year = now.getFullYear();
      const date = now.toISOString().split('T')[0];
      const time = now.toISOString().replace(/[:.]/g, '-').split('.')[0];
      const fileExtension = selectedAudioFile.name.split('.').pop();
      const fileName = selectedAudioFile.name.replace(/\.[^/.]+$/, "");
      
      const s3Key = `users/${user.username}/${year}/${date}/${time}-${fileName}.${fileExtension}`;
      
      setStatus('📁 Converting file...');
      
      // Convert file to ArrayBuffer
      const fileBuffer = await selectedAudioFile.arrayBuffer();

      setStatus('📁 Uploading to S3...');
      setUploadProgress(0);
      
      // Real multipart upload with progress tracking
      const s3Client = new S3Client({
        region: 'us-east-1',
        credentials: {
          accessKeyId: credentials.accessKeyId,
          secretAccessKey: credentials.secretAccessKey,
          sessionToken: credentials.sessionToken
        }
      });

      const totalMB = (selectedAudioFile.size / 1024 / 1024).toFixed(2);
      
      // Initialize multipart upload
      const createResponse = await s3Client.send(new CreateMultipartUploadCommand({
        Bucket: 'live-translate-tanisorn-dot-work',
        Key: s3Key,
        ContentType: selectedAudioFile.type || 'audio/mpeg'
      }));
      
      const uploadId = createResponse.UploadId;
      let parts = [];
      
      try {
        // Split file into chunks (5MB minimum for S3)
        const chunkSize = Math.max(5 * 1024 * 1024, Math.ceil(fileBuffer.byteLength / 100)); // At least 5MB or 1% of file
        const chunks = [];
        
        for (let start = 0; start < fileBuffer.byteLength; start += chunkSize) {
          chunks.push(fileBuffer.slice(start, Math.min(start + chunkSize, fileBuffer.byteLength)));
        }
        
        console.log(`Uploading ${chunks.length} parts, ${(chunkSize / 1024 / 1024).toFixed(2)}MB each`);
        
        // Upload parts with real progress tracking
        let uploadedBytes = 0;
        
        for (let i = 0; i < chunks.length; i++) {
          const partResponse = await s3Client.send(new UploadPartCommand({
            Bucket: 'live-translate-tanisorn-dot-work',
            Key: s3Key,
            PartNumber: i + 1,
            UploadId: uploadId,
            Body: new Uint8Array(chunks[i])
          }));
          
          parts.push({
            ETag: partResponse.ETag,
            PartNumber: i + 1
          });
          
          // Real progress tracking
          uploadedBytes += chunks[i].byteLength;
          const progress = Math.round((uploadedBytes / fileBuffer.byteLength) * 100);
          const uploadedMB = (uploadedBytes / 1024 / 1024).toFixed(2);
          
          setUploadProgress(progress);
          setStatus(`📁 Uploading to S3... ${uploadedMB}MB / ${totalMB}MB (${progress}%)`);
        }
        
        // Complete multipart upload
        await s3Client.send(new CompleteMultipartUploadCommand({
          Bucket: 'live-translate-tanisorn-dot-work',
          Key: s3Key,
          UploadId: uploadId,
          MultipartUpload: { Parts: parts }
        }));
        
        setUploadProgress(100);
        setStatus(`📁 Upload complete! ${totalMB}MB / ${totalMB}MB (100%)`);
        
      } catch (uploadError) {
        // Abort multipart upload on error
        try {
          await s3Client.send(new AbortMultipartUploadCommand({
            Bucket: 'live-translate-tanisorn-dot-work',
            Key: s3Key,
            UploadId: uploadId
          }));
        } catch (abortError) {
          console.error('Failed to abort multipart upload:', abortError);
        }
        throw uploadError;
      }
      
      // Brief pause to show completion
      await new Promise(resolve => setTimeout(resolve, 500));

      setStatus('✅ File uploaded successfully to S3!');

      // Start transcription job
      setStatus('🎙️ Starting transcription with speaker diarization...');
      
      const transcribeClient = new TranscribeClient({
        region: 'us-east-1',
        credentials: {
          accessKeyId: credentials.accessKeyId,
          secretAccessKey: credentials.secretAccessKey,
          sessionToken: credentials.sessionToken
        }
      });

      const jobName = `transcription-${Date.now()}`;
      const mediaUri = `s3://live-translate-tanisorn-dot-work/${s3Key}`;

      console.log('Starting transcription job:', jobName);
      console.log('Media URI:', mediaUri);

      // Map source language to correct Transcribe language code
      const getTranscribeLanguageCode = (lang) => {
        if (lang === 'auto') return null; // Auto-detect language
        
        const languageMap = {
          'en': 'en-US',
          'es': 'es-US', 
          'fr': 'fr-FR',
          'de': 'de-DE',
          'it': 'it-IT',
          'pt': 'pt-BR',
          'ru': 'ru-RU',
          'ja': 'ja-JP',
          'ko': 'ko-KR',
          'zh': 'zh-CN',
          'ar': 'ar-SA',
          'hi': 'hi-IN',
          'th': 'th-TH',
          'vi': 'vi-VN',
          'tr': 'tr-TR',
          'pl': 'pl-PL',
          'nl': 'nl-NL',
          'sv': 'sv-SE',
          'da': 'da-DK',
          'no': 'no-NO',
          'fi': 'fi-FI'
        };
        return languageMap[lang] || 'en-US';
      };

      const transcribeParams = {
        TranscriptionJobName: jobName,
        Media: { MediaFileUri: mediaUri },
        OutputBucketName: 'live-translate-tanisorn-dot-work',
        OutputKey: `transcriptions/${user.username}/${year}/${date}/`,
        Settings: {
          ShowSpeakerLabels: true,
          MaxSpeakerLabels: 10
        }
      };

      // Add language code only if not auto-detect
      const languageCode = getTranscribeLanguageCode(sourceLanguage);
      if (languageCode) {
        transcribeParams.LanguageCode = languageCode;
      } else {
        transcribeParams.IdentifyLanguage = true; // Enable auto language detection
      }

      await transcribeClient.send(new StartTranscriptionJobCommand(transcribeParams));

      setStatus('⏳ Transcription in progress...');
      console.log('Transcription job started, polling for completion...');

      // Function to process transcript data
      const processTranscriptData = async (transcriptData, fullTranscript) => {
        // Format with speaker labels if available
        let displayText = fullTranscript;
        if (transcriptData.results.speaker_labels) {
          displayText = transcriptData.results.speaker_labels.segments.map(segment => {
            const startTime = parseFloat(segment.start_time);
            const minutes = Math.floor(startTime / 60);
            const seconds = Math.floor(startTime % 60);
            
            // Get text for this segment
            const segmentText = segment.items.map(item => {
              const matchingItem = transcriptData.results.items.find(i => 
                Math.abs(parseFloat(i.start_time || 0) - parseFloat(item.start_time || 0)) < 0.1
              );
              return matchingItem?.alternatives?.[0]?.content || '';
            }).join(' ');
            
            return `[${segment.speaker_label.toUpperCase()} ${minutes}:${seconds.toString().padStart(2,'0')}]: ${segmentText}`;
          }).join('\n\n');
        }
        
        setTranscribedText(displayText);
        
        // Auto-translate if real-time mode is enabled
        if (realTimeMode) {
          setStatus('🔄 Real-time translation enabled - translating transcript...');
          
          try {
            const translation = await translationService.current.translateText(
              displayText, 
              sourceLanguage, 
              targetLanguage, 
              false
            );
            
            updateTranslatedText(translation);
            setStatus('✅ Audio transcription and translation completed!');
            setIsProcessing(false);
            
            // Update token usage
            const usage = translationService.current.getTokenUsage();
            setTokenUsage(usage);
            
            // Add to history
            setTranslationHistory(prev => [...prev, {
              id: Date.now(),
              timestamp: new Date(),
              source: displayText,
              translation,
              sourceLanguage,
              targetLanguage,
              model: selectedModel,
              type: 'audio_file',
              fileName: selectedAudioFile.name,
              speakers: transcriptData.results.speaker_labels?.speakers || 1
            }]);
            
          } catch (translateError) {
            console.error('Translation error:', translateError);
            setStatus('⚠️ Transcription completed, but translation failed');
            setError(`Translation failed: ${translateError.message}`);
          }
        } else {
          setStatus('✅ Transcription completed successfully!');
        }
      };

      // Poll for completion with real-time streaming
      const pollTranscription = async (attempts = 0) => {
        if (attempts > 60) {
          throw new Error('Transcription timeout after 5 minutes');
        }
        
        setStatus(`⏳ Transcription in progress... (${attempts + 1}/60)`);
        
        const response = await transcribeClient.send(new GetTranscriptionJobCommand({
          TranscriptionJobName: jobName
        }));
        
        const jobStatus = response.TranscriptionJob.TranscriptionJobStatus;
        
        if (jobStatus === 'COMPLETED') {
          setStatus('🎉 Transcription completed! Processing results...');
          
          const transcriptUri = response.TranscriptionJob.Transcript.TranscriptFileUri;
          console.log('Full transcript URI:', transcriptUri);
          
          try {
            // Extract S3 key from the URI - remove bucket name from path
            const s3Url = new URL(transcriptUri);
            const fullPath = s3Url.pathname.substring(1); // Remove leading slash
            // Remove bucket name from the beginning of the path
            const transcriptKey = fullPath.replace('live-translate-tanisorn-dot-work/', '');
            console.log('Extracted S3 key:', transcriptKey);
            
            // Create S3 client with fresh credentials for transcript fetch
            const transcriptS3Client = new S3Client({
              region: 'us-east-1',
              credentials: {
                accessKeyId: credentials.accessKeyId,
                secretAccessKey: credentials.secretAccessKey,
                sessionToken: credentials.sessionToken
              }
            });
            
            // Use S3 client to get the transcript with credentials
            const getObjectCommand = new GetObjectCommand({
              Bucket: 'live-translate-tanisorn-dot-work',
              Key: transcriptKey
            });
            
            console.log('Fetching transcript from S3...');
            const transcriptObject = await transcriptS3Client.send(getObjectCommand);
            const transcriptText = await transcriptObject.Body.transformToString();
            const transcriptData = JSON.parse(transcriptText);
            const fullTranscript = transcriptData.results.transcripts[0].transcript;
            
            // Extract detected language from Transcribe results
            let detectedLanguage = null;
            if (transcriptData.results && transcriptData.results.language_identification) {
              const langIdentification = transcriptData.results.language_identification[0];
              if (langIdentification && langIdentification.code) {
                // Map Transcribe language codes back to our language codes
                const transcribeToOurLangMap = {
                  'en-US': 'en', 'en-GB': 'en', 'en-AU': 'en',
                  'es-US': 'es', 'es-ES': 'es',
                  'fr-FR': 'fr', 'fr-CA': 'fr',
                  'de-DE': 'de',
                  'it-IT': 'it',
                  'pt-BR': 'pt', 'pt-PT': 'pt',
                  'ru-RU': 'ru',
                  'ja-JP': 'ja',
                  'ko-KR': 'ko',
                  'zh-CN': 'zh',
                  'ar-SA': 'ar',
                  'hi-IN': 'hi',
                  'th-TH': 'th',
                  'vi-VN': 'vi',
                  'tr-TR': 'tr',
                  'pl-PL': 'pl',
                  'nl-NL': 'nl',
                  'sv-SE': 'sv',
                  'da-DK': 'da',
                  'no-NO': 'no',
                  'fi-FI': 'fi'
                };
                
                detectedLanguage = transcribeToOurLangMap[langIdentification.code];
                console.log('Detected language:', langIdentification.code, '→', detectedLanguage);
                
                // Update source language to detected language
                if (detectedLanguage && detectedLanguage !== sourceLanguage) {
                  setSourceLanguage(detectedLanguage);
                  console.log('Updated source language to detected:', detectedLanguage);
                }
              }
            }
            
            // Process the transcript
            await processTranscriptData(transcriptData, fullTranscript);
            
          } catch (error) {
            console.error('Failed to fetch transcript:', error);
            console.error('Transcript URI:', transcriptUri);
            console.error('Error details:', {
              name: error.name,
              message: error.message,
              code: error.code || 'Unknown'
            });
            
            // More user-friendly error message
            let errorMessage = 'Failed to retrieve transcription results';
            if (error.name === 'NoSuchKey') {
              errorMessage = 'Transcription file not found. Please try again.';
            } else if (error.name === 'AccessDenied') {
              errorMessage = 'Access denied to transcription results. Please check permissions.';
            } else if (error.message.includes('Load failed')) {
              errorMessage = 'Network error while fetching results. Please check your connection.';
            }
            
            setStatus(`❌ ${errorMessage}`);
            setError(errorMessage);
            return; // Don't throw, just set error status
          }
        } else if (jobStatus === 'FAILED') {
          const failureReason = response.TranscriptionJob.FailureReason || 'Unknown error';
          console.error('Transcription failed:', failureReason);
          
          let userFriendlyError = failureReason;
          if (failureReason.includes('Failed to parse audio file')) {
            userFriendlyError = 'Audio file format not supported or corrupted. Try converting to MP3 or WAV format.';
          } else if (failureReason.includes('duration')) {
            userFriendlyError = 'Audio file is too long. Maximum duration is 4 hours.';
          } else if (failureReason.includes('size')) {
            userFriendlyError = 'Audio file is too large. Maximum size is 2GB.';
          }
          
          throw new Error(userFriendlyError);
        } else {
          // Job still in progress, poll again in 5 seconds
          setTimeout(() => pollTranscription(attempts + 1), 5000);
        }
      };
      
      // Start polling after 10 seconds (give job time to start)
      setTimeout(() => pollTranscription(), 10000);
      
    } catch (error) {
      console.error('Upload error details:', {
        message: error.message,
        name: error.name,
        stack: error.stack,
        code: error.code
      });
      
      setStatus(`❌ Upload failed: ${error.message}`);
      setError(`S3 upload failed: ${error.message}`);
    } finally {
      setIsProcessingAudio(false);
      setUploadProgress(0);
    }
  };

  const translateManualText = useCallback(async () => {
    if (!manualText.trim()) return;
    
    setIsProcessing(true);
    setTranscribedText(manualText);
    await translateText(manualText, false);
    setManualText('');
    setIsProcessing(false);
  }, [manualText, translateText]);

  // Auto-reset source language if 'auto' is selected but no file is selected
  useEffect(() => {
    if (sourceLanguage === 'auto' && !selectedAudioFile) {
      // Auto-detect is only for file uploads, reset to Thai when no file
      setSourceLanguage('th');
    }
  }, [sourceLanguage, selectedAudioFile, setSourceLanguage]);

  // Manual translate from transcribed text
  const translateFromTranscribed = useCallback(async () => {
    if (!transcribedText.trim()) return;
    
    await translateText(transcribedText, false);
  }, [transcribedText, translateText]);

  // Auto-translate when target language changes
  useEffect(() => {
    if (transcribedText.trim() && isInitialized && !isRecording) {
      translateFromTranscribed();
    }
  }, [targetLanguage, translateFromTranscribed, transcribedText, isInitialized, isRecording]);

  // Update translation service when showReasoning changes
  useEffect(() => {
    if (translationService.current) {
      translationService.current.setShowReasoning(showReasoning);
      
      // Re-process current translation if it exists
      if (originalTranslatedText) {
        if (showReasoning) {
          // Show full original text with reasoning
          setTranslatedText(originalTranslatedText);
        } else {
          // Hide reasoning from original text
          if (originalTranslatedText.includes('<reasoning>') && originalTranslatedText.includes('</reasoning>')) {
            const reasoningEnd = originalTranslatedText.indexOf('</reasoning>');
            let cleanText = originalTranslatedText.substring(reasoningEnd + 12).trim();
            if (cleanText.startsWith('"') && cleanText.endsWith('"')) {
              cleanText = cleanText.slice(1, -1);
            }
            setTranslatedText(cleanText);
          } else {
            setTranslatedText(originalTranslatedText);
          }
        }
      }
    }
  }, [showReasoning, originalTranslatedText]);

  return (
    <div className="App" style={{ padding: '0 10px', maxWidth: '1200px', margin: '0 auto' }}>
      <style>{`
        /* Prevent iOS Safari zoom on input focus - More aggressive approach */
        input, textarea, select {
          font-size: 16px !important;
          -webkit-text-size-adjust: 100% !important;
          -webkit-appearance: none !important;
        }
        
        /* Specific targeting for manual text areas */
        .manual-text-container textarea {
          font-size: 16px !important;
        }
        
        /* Target all form elements */
        input[type="text"], 
        input[type="email"], 
        input[type="password"], 
        input[type="search"], 
        input[type="tel"], 
        input[type="url"], 
        textarea, 
        select {
          font-size: 16px !important;
        }
        
        /* Mobile tab system */
        .mobile-tabs {
          display: none;
        }
        
        .mobile-tab-content > div {
          display: none;
        }
        
        .mobile-tab-content > div.active {
          display: flex;
        }
        
        @media (max-width: 768px) {
          .App {
            padding: 0 5px !important;
          }
          
          /* Mobile tabs */
          .mobile-tabs {
            display: flex !important;
            justify-content: center;
            gap: 5px;
            margin-bottom: 15px;
            background: #f8f9fa;
            padding: 5px;
            border-radius: 8px;
          }
          
          .mobile-tab {
            padding: 8px 12px;
            background: white;
            border: 1px solid #ddd;
            border-radius: 6px;
            cursor: pointer;
            font-size: 12px;
            transition: all 0.2s;
          }
          
          .mobile-tab.active {
            background: #007bff;
            color: white;
            border-color: #007bff;
          }
          
          .desktop-grid {
            display: none !important;
          }
          
          .mobile-tab-content {
            display: block !important;
          }
          
          /* Make buttons stack on mobile */
          .mobile-stack {
            flex-direction: column !important;
            align-items: stretch !important;
          }
          
          .mobile-stack button {
            margin-bottom: 10px;
          }
          
          /* Adjust text sizes on mobile */
          h3 {
            font-size: 1em !important;
          }
          
          /* Better spacing on mobile */
          .grid-container {
            gap: 15px !important;
            margin: 15px 0 !important;
          }
          
          /* Make manual text input stack on mobile */
          .manual-text-container {
            flex-direction: column !important;
            align-items: stretch !important;
          }
          
          .manual-text-container textarea {
            margin-bottom: 10px !important;
            min-width: auto !important;
            rows: 2 !important;
            min-height: 60px !important;
          }
          
          .manual-text-container button {
            align-self: center !important;
            width: auto !important;
          }
        }
        
        @media (max-width: 480px) {
          .App {
            padding: 0 2px !important;
          }
          
          .grid-container {
            gap: 10px !important;
            margin: 10px 0 !important;
          }
          
          /* Smaller grid minimum on very small screens */
          .grid-container {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
      {/* Error Display - Hidden since we show it in Recording Controls */}
      {false && error && (
        <div className="error">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Three Control Sections */}
      {/* Mobile Tab Navigation */}
      <div className="mobile-tabs">
        <div 
          className={`mobile-tab ${activeTab === 'recording' ? 'active' : ''}`}
          onClick={() => setActiveTab('recording')}
        >
          🎤 Recording
        </div>
        <div 
          className={`mobile-tab ${activeTab === 'manual' ? 'active' : ''}`}
          onClick={() => setActiveTab('manual')}
        >
          ✍️ Manual
        </div>
        <div 
          className={`mobile-tab ${activeTab === 'file' ? 'active' : ''}`}
          onClick={() => setActiveTab('file')}
        >
          🎵 File
        </div>
      </div>

      {/* Desktop Grid */}
      <div className="desktop-grid grid-container" style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
        gap: '20px', 
        margin: '20px 0' 
      }}>
        {/* Section 1 - Recording Controls */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          border: '1px solid #e9ecef',
          textAlign: 'center',
          opacity: manualText.trim() || selectedAudioFile ? 0.5 : 1,
          pointerEvents: manualText.trim() || selectedAudioFile ? 'none' : 'auto',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '200px'
        }}>
          <h3 style={{ margin: '0 0 15px 0', color: '#333', fontSize: '1.1em' }}>🎤 Recording Controls</h3>
          
          {/* Error Display within Recording Controls */}
          {error && (
            <div style={{
              margin: '0 0 15px 0',
              padding: '12px',
              backgroundColor: '#ffebee',
              borderRadius: '8px',
              border: '1px solid #f44336'
            }}>
              <div style={{ 
                fontSize: '14px', 
                color: '#c62828', 
                fontWeight: '500',
                textAlign: 'center'
              }}>
                ❌ {error}
              </div>
            </div>
          )}
          
          <div className="mobile-stack" style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap', flex: 1, alignItems: 'center' }}>
            {!isRecording ? (
              <button 
                onClick={startRecording} 
                disabled={!isInitialized}
                style={{
                  fontSize: '14px',
                  padding: '12px 20px',
                  backgroundColor: isInitialized ? '#10b981' : '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: isInitialized ? 'pointer' : 'not-allowed',
                  fontWeight: '600',
                  transition: 'all 0.3s ease'
                }}
              >
                🎤 Start Recording
              </button>
            ) : (
              <button 
                onClick={stopRecording}
                style={{
                  fontSize: '14px',
                  padding: '12px 20px',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  transition: 'all 0.3s ease'
                }}
              >
                ⏹️ Stop Recording
              </button>
            )}
          </div>
          
          {/* Real-time Translation Toggle */}
          <div style={{ marginTop: '15px', textAlign: 'center' }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              gap: '8px',
              fontSize: '14px',
              color: '#666',
              cursor: 'pointer',
              fontWeight: '500'
            }}>
              <input
                id="realtime-mode-1"
                name="realtime-mode-1"
                type="checkbox"
                checked={realTimeMode}
                onChange={(e) => setRealTimeMode(e.target.checked)}
                style={{ 
                  transform: 'scale(1.2)'
                }}
              />
              Enable Real-time Translation
            </label>
            <div style={{ fontSize: '11px', color: '#666', marginTop: '3px', height: '14px' }}>
              {realTimeMode 
                ? '⚡ Instant translation, higher token usage' 
                : '⏱️ Translates after speaking, lower token usage'
              }
            </div>
          </div>
        </div>

        {/* Section 2 - Manual Text Input */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          border: '1px solid #e9ecef',
          opacity: isRecording || selectedAudioFile ? 0.5 : 1,
          pointerEvents: isRecording || selectedAudioFile ? 'none' : 'auto',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '200px'
        }}>
          <h3 style={{ margin: '0 0 15px 0', color: '#333', fontSize: '1.1em' }}>✍️ Manual Text Input</h3>
          <div className="manual-text-container" style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', flexWrap: 'wrap', flex: 1, justifyContent: 'center' }}>
            <textarea
              value={manualText}
              onChange={(e) => setManualText(e.target.value)}
              placeholder="Type text to translate..."
              rows="3"
              disabled={isRecording}
              style={{
                flex: '1 1 200px',
                minWidth: '200px',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px',
                resize: 'vertical',
                fontFamily: 'inherit',
                backgroundColor: 'white'
              }}
            />
            <button 
              onClick={translateManualText}
              disabled={!manualText.trim() || !isInitialized || isRecording}
              style={{
                padding: '12px 20px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: manualText.trim() && isInitialized && !isRecording ? 'pointer' : 'not-allowed',
                fontSize: '14px',
                fontWeight: '500',
                opacity: manualText.trim() && isInitialized && !isRecording ? 1 : 0.6,
                whiteSpace: 'nowrap',
                flexShrink: 0
              }}
            >
              🔄 Translate
            </button>
          </div>
        </div>

        {/* Section 3 - Audio File Upload */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          border: '1px solid #e9ecef',
          opacity: isRecording || manualText.trim() ? 0.5 : 1,
          pointerEvents: isRecording || manualText.trim() ? 'none' : 'auto',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '200px'
        }}>
          <h3 style={{ margin: '0 0 15px 0', color: '#333', fontSize: '1.1em' }}>🎵 Audio File Upload</h3>
          
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          {/* Upload Progress Status - Moved here */}
          {(isProcessingAudio || status.includes('Transcription')) && (
            <div style={{
              margin: '0 0 15px 0',
              padding: '12px',
              backgroundColor: status.includes('Transcription') ? '#f3e5f5' : '#e3f2fd',
              borderRadius: '8px',
              border: status.includes('Transcription') ? '1px solid #9c27b0' : '1px solid #2196f3'
            }}>
              <div style={{ 
                fontSize: '14px', 
                color: status.includes('Transcription') ? '#7b1fa2' : '#1976d2', 
                fontWeight: '500',
                marginBottom: '8px'
              }}>
                {status}
                {(status.includes('Transcription') || status.includes('Uploading to S3') || status.includes('Starting transcription') || status.includes('Preparing upload') || status.includes('Converting file') || status.includes('Upload complete') || status.includes('Processing results') || status.includes('Real-time translation enabled')) && selectedAudioFile && (
                  <div style={{ 
                    fontSize: '13px', 
                    color: status.includes('Transcription') ? '#7b1fa2' : '#1976d2', 
                    fontWeight: '400',
                    marginTop: '4px'
                  }}>
                    📎 {selectedAudioFile.name} ({(selectedAudioFile.size / 1024 / 1024).toFixed(2)} MB)
                  </div>
                )}
              </div>
              <div style={{
                width: '100%',
                height: '4px',
                backgroundColor: status.includes('Transcription') ? '#e1bee7' : '#bbdefb',
                borderRadius: '2px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: status.includes('Uploading to S3') ? `${uploadProgress}%` : '100%',
                  height: '100%',
                  backgroundColor: status.includes('Transcription') ? '#9c27b0' : '#2196f3',
                  transition: 'width 0.3s ease',
                  animation: status.includes('Uploading to S3') ? 'none' : 'progress-bar 2s ease-in-out infinite'
                }}></div>
              </div>
            </div>
          )}
          
          <div style={{ textAlign: 'center' }}>
            <input
              id="file-upload-1"
              name="file-upload-1"
              ref={fileInputRef}
              type="file"
              accept=".amr,.flac,.m4a,.mp3,.mp4,.ogg,.webm,.wav"
              onChange={handleFileSelect}
              disabled={isProcessingAudio}
              style={{ display: 'none' }}
              id="audio-file-input"
              name="audio-file-input"
            />
            <div className="mobile-stack" style={{ display: 'flex', gap: '10px', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
              {!isProcessingAudio && !status.includes('Transcription') && (
                <label
                  htmlFor="audio-file-input"
                  style={{
                    display: 'inline-block',
                    padding: '10px 16px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500',
                    transition: 'all 0.3s ease'
                  }}
                >
                  📁 Choose File
                </label>
              )}
            </div>
            
            {selectedAudioFile && !status.includes('Transcription') && !status.includes('Uploading to S3') && !status.includes('SUCCESS') && !status.includes('Starting transcription') && !status.includes('Preparing upload') && !status.includes('Converting file') && !status.includes('Upload complete') && !status.includes('Processing results') && !status.includes('Real-time translation enabled') && (
              <>
                <style>{`
                  @keyframes slideInDown {
                    from {
                      transform: translateY(-20px);
                      opacity: 0;
                    }
                    to {
                      transform: translateY(0);
                      opacity: 1;
                    }
                  }
                  .file-selected {
                    animation: slideInDown 0.4s ease-out;
                  }
                `}</style>
                <p className="file-selected" style={{ fontSize: '13px', color: '#28a745', margin: '8px 0 0 0' }}>
                  📎 {selectedAudioFile.name} ({(selectedAudioFile.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              </>
            )}
            
            {!isProcessingAudio && !status.includes('Transcription') && (
              <>
                <p style={{ fontSize: '12px', color: '#666', margin: '8px 0 4px 0' }}>
                  🤖 Amazon Transcribe auto-detects language
                </p>
                <p style={{ fontSize: '12px', color: '#666', margin: '0 0 0 0' }}>
                  Supports: AMR, FLAC, M4A, MP3, MP4, OGG, WebM, WAV
                </p>
              </>
            )}
            
            {selectedAudioFile && !isProcessingAudio && !status.includes('Transcription') && (
              <div className="file-selected" style={{ textAlign: 'center', margin: '10px 0 0 0' }}>
                <button
                  onClick={handleAudioFileUpload}
                  style={{
                    padding: '10px 16px',
                    backgroundColor: '#17a2b8',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500',
                    transition: 'all 0.3s ease',
                    transform: 'scale(1)',
                    marginBottom: '8px'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.transform = 'scale(1.05)';
                    e.target.style.backgroundColor = '#138496';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.transform = 'scale(1)';
                    e.target.style.backgroundColor = '#17a2b8';
                  }}
                >
                  🚀 Upload & Transcribe
                </button>
                <div>
                  <button
                    onClick={handleFileRemoval}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: 'transparent',
                      color: '#6c757d',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '11px',
                      textDecoration: 'underline'
                    }}
                  >
                    Clear selected file
                  </button>
                </div>
              </div>
            )}
            {/* Success message display */}
            {status.includes('SUCCESS') && (
              <p style={{ fontSize: '13px', color: '#28a745', margin: '8px 0 0 0', fontWeight: '600' }}>
                ✅ SUCCESS! File size: {selectedAudioFile ? (selectedAudioFile.size / 1024 / 1024).toFixed(2) : '0'} MB
              </p>
            )}
          </div>
          </div>
        </div>
      </div>

      {/* Mobile Tab Content */}
      <div className="mobile-tab-content" style={{ display: 'none' }}>
        {/* Recording Controls Mobile */}
        <div className={activeTab === 'recording' ? 'active' : ''} style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          border: '1px solid #e9ecef',
          textAlign: 'center',
          opacity: manualText.trim() || selectedAudioFile ? 0.5 : 1,
          pointerEvents: manualText.trim() || selectedAudioFile ? 'none' : 'auto',
          flexDirection: 'column',
          minHeight: '200px'
        }}>
          <h3 style={{ margin: '0 0 15px 0', color: '#333', fontSize: '1.1em' }}>🎤 Recording Controls</h3>
          
          {/* Error Display within Recording Controls */}
          {error && (
            <div style={{
              margin: '0 0 15px 0',
              padding: '12px',
              backgroundColor: '#ffebee',
              borderRadius: '8px',
              border: '1px solid #f44336'
            }}>
              <div style={{ 
                fontSize: '14px', 
                color: '#c62828', 
                fontWeight: '500',
                textAlign: 'center'
              }}>
                ❌ {error}
              </div>
            </div>
          )}
          
          <div className="mobile-stack" style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap', flex: 1, alignItems: 'center' }}>
            {!isRecording ? (
              <button 
                onClick={startRecording}
                disabled={!isInitialized}
                style={{
                  padding: '12px 24px',
                  backgroundColor: isInitialized ? '#28a745' : '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isInitialized ? 'pointer' : 'not-allowed',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                🎤 Start Recording
              </button>
            ) : (
              <button 
                onClick={stopRecording}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                ⏹️ Stop Recording
              </button>
            )}
          </div>
          
          {/* Real-time Translation Toggle */}
          <div style={{ marginTop: '15px', textAlign: 'center' }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              gap: '8px',
              fontSize: '14px',
              color: '#666',
              cursor: 'pointer',
              fontWeight: '500'
            }}>
              <input
                id="realtime-mode-2"
                name="realtime-mode-2"
                type="checkbox"
                checked={realTimeMode}
                onChange={(e) => setRealTimeMode(e.target.checked)}
                style={{ 
                  transform: 'scale(1.2)'
                }}
              />
              Enable Real-time Translation
            </label>
            <div style={{ fontSize: '11px', color: '#666', marginTop: '3px', height: '14px' }}>
              {realTimeMode 
                ? '⚡ Instant translation, higher token usage' 
                : '⏱️ Translates after speaking, lower token usage'
              }
            </div>
          </div>
        </div>

        {/* Manual Text Input Mobile */}
        <div className={activeTab === 'manual' ? 'active' : ''} style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          border: '1px solid #e9ecef',
          opacity: isRecording || selectedAudioFile ? 0.5 : 1,
          pointerEvents: isRecording || selectedAudioFile ? 'none' : 'auto',
          flexDirection: 'column',
          minHeight: '200px'
        }}>
          <h3 style={{ margin: '0 0 15px 0', color: '#333', fontSize: '1.1em' }}>✍️ Manual Text Input</h3>
          <div className="manual-text-container" style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', flexWrap: 'wrap', flex: 1, justifyContent: 'center' }}>
            <textarea
              value={manualText}
              onChange={(e) => setManualText(e.target.value)}
              placeholder="Type text to translate..."
              rows="3"
              disabled={isRecording}
              style={{
                flex: '1 1 200px',
                minWidth: '200px',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px',
                resize: 'vertical',
                fontFamily: 'inherit',
                backgroundColor: 'white'
              }}
            />
            <button 
              onClick={translateManualText}
              disabled={!manualText.trim() || !isInitialized || isRecording}
              style={{
                padding: '12px 20px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: manualText.trim() && isInitialized && !isRecording ? 'pointer' : 'not-allowed',
                fontSize: '14px',
                fontWeight: '500',
                opacity: manualText.trim() && isInitialized && !isRecording ? 1 : 0.6,
                whiteSpace: 'nowrap',
                flexShrink: 0
              }}
            >
              🔄 Translate
            </button>
          </div>
        </div>

        {/* Audio File Upload Mobile */}
        <div className={activeTab === 'file' ? 'active' : ''} style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          border: '1px solid #e9ecef',
          opacity: isRecording || manualText.trim() ? 0.5 : 1,
          pointerEvents: isRecording || manualText.trim() ? 'none' : 'auto',
          flexDirection: 'column',
          minHeight: '200px'
        }}>
          <h3 style={{ margin: '0 0 15px 0', color: '#333', fontSize: '1.1em' }}>🎵 Audio File Upload</h3>
          
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          {/* Upload Progress Status - Moved here */}
          {(isProcessingAudio || status.includes('Transcription')) && (
            <div style={{
              margin: '0 0 15px 0',
              padding: '12px',
              backgroundColor: status.includes('Transcription') ? '#f3e5f5' : '#e3f2fd',
              borderRadius: '8px',
              border: status.includes('Transcription') ? '1px solid #9c27b0' : '1px solid #2196f3'
            }}>
              <div style={{ 
                fontSize: '14px', 
                color: status.includes('Transcription') ? '#7b1fa2' : '#1976d2', 
                fontWeight: '500',
                textAlign: 'center'
              }}>
                {status}
                {(status.includes('Transcription') || status.includes('Uploading to S3') || status.includes('Starting transcription') || status.includes('Preparing upload') || status.includes('Converting file') || status.includes('Upload complete') || status.includes('Processing results') || status.includes('Real-time translation enabled')) && selectedAudioFile && (
                  <div style={{ 
                    fontSize: '13px', 
                    color: status.includes('Transcription') ? '#9c27b0' : '#2196f3',
                    fontWeight: 'normal',
                    marginTop: '4px'
                  }}>
                    📎 {selectedAudioFile.name} ({(selectedAudioFile.size / 1024 / 1024).toFixed(2)} MB)
                  </div>
                )}
              </div>
              <div style={{
                width: '100%',
                height: '6px',
                backgroundColor: 'rgba(255,255,255,0.3)',
                borderRadius: '3px',
                marginTop: '8px',
                overflow: 'hidden'
              }}>
                <div style={{
                  height: '100%',
                  backgroundColor: status.includes('Transcription') ? '#9c27b0' : '#2196f3',
                  width: status.includes('Uploading to S3') ? '50%' : status.includes('Transcription') ? '75%' : '100%',
                  borderRadius: '3px',
                  transition: 'width 0.3s ease',
                  animation: status.includes('Uploading to S3') ? 'none' : 'progress-bar 2s ease-in-out infinite'
                }}></div>
              </div>
            </div>
          )}
          
          <input
            id="file-upload-2"
            name="file-upload-2"
            ref={fileInputRef}
            type="file"
            accept=".amr,.flac,.m4a,.mp3,.mp4,.ogg,.webm,.wav"
            onChange={handleFileSelect}
            disabled={isProcessingAudio}
            style={{ display: 'none' }}
            id="audio-file-input"
            name="audio-file-input"
          />
          <div className="mobile-stack" style={{ display: 'flex', gap: '10px', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
            {!isProcessingAudio && !status.includes('Transcription') && (
              <label
                htmlFor="audio-file-input"
                style={{
                  padding: '12px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'inline-block'
                }}
              >
                📁 Choose File
              </label>
            )}
            
            {selectedAudioFile && !status.includes('Transcription') && !status.includes('Uploading to S3') && !status.includes('SUCCESS') && !status.includes('Starting transcription') && !status.includes('Preparing upload') && !status.includes('Converting file') && !status.includes('Upload complete') && !status.includes('Processing results') && !status.includes('Real-time translation enabled') && (
              <>
                <style>{`
                  @keyframes slideInDown {
                    from {
                      opacity: 0;
                      transform: translateY(-10px);
                    }
                    to {
                      opacity: 1;
                      transform: translateY(0);
                    }
                  }
                  .file-selected {
                    animation: slideInDown 0.3s ease-out;
                  }
                `}</style>
                <p className="file-selected" style={{ fontSize: '13px', color: '#28a745', margin: '8px 0 0 0' }}>
                  📎 {selectedAudioFile.name} ({(selectedAudioFile.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              </>
            )}
            
            {selectedAudioFile && !isProcessingAudio && !status.includes('Transcription') && (
              <div className="file-selected" style={{ textAlign: 'center', margin: '10px 0 0 0' }}>
                <button
                  onClick={handleAudioFileUpload}
                  disabled={!selectedAudioFile}
                  style={{
                    padding: '12px 20px',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: selectedAudioFile ? 'pointer' : 'not-allowed',
                    fontSize: '14px',
                    fontWeight: '500',
                    opacity: selectedAudioFile ? 1 : 0.6,
                    marginRight: '10px'
                  }}
                >
                  🚀 Upload & Transcribe
                </button>
                <div style={{ marginTop: '10px' }}>
                  <button
                    onClick={handleFileRemoval}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: 'transparent',
                      color: '#dc3545',
                      border: '1px solid #dc3545',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}
                  >
                    Clear selected file
                  </button>
                </div>
              </div>
            )}
            {/* Success message display */}
            {status.includes('SUCCESS') && (
              <p style={{ fontSize: '13px', color: '#28a745', margin: '8px 0 0 0', fontWeight: '600' }}>
                ✅ SUCCESS! File size: {selectedAudioFile ? (selectedAudioFile.size / 1024 / 1024).toFixed(2) : '0'} MB
              </p>
            )}
          </div>
          
          {!isProcessingAudio && !status.includes('Transcription') && (
            <>
              <p style={{ fontSize: '12px', color: '#666', margin: '8px 0 4px 0' }}>
                🤖 Amazon Transcribe auto-detects language
              </p>
              <p style={{ fontSize: '12px', color: '#666', margin: '0 0 0 0' }}>
                Supports: AMR, FLAC, M4A, MP3, MP4, OGG, WebM, WAV
              </p>
            </>
          )}
          </div>
        </div>
      </div>

      {/* Results Display */}
      <div className="results">
        <div className="result-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
            <h3 style={{ margin: 0, color: '#333', fontSize: '1.1em' }}>
              Transcribed Text:
              {isRecording && (
                <span className="recording-indicator" style={{marginLeft: '10px'}}>
                  <div className="recording-dot"></div>
                  <span className="recording-text">LISTENING</span>
                </span>
              )}
            </h3>
            <button 
              onClick={clearText}
              disabled={isRecording}
              style={{
                fontSize: '12px',
                padding: '6px 12px',
                backgroundColor: isRecording ? '#6c757d' : '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: isRecording ? 'not-allowed' : 'pointer',
                fontWeight: '500',
                transition: 'all 0.3s ease',
                opacity: isRecording ? 0.5 : 1
              }}
            >
              Clear
            </button>
          </div>
          {/* Source Language Selector */}
          <div style={{ marginBottom: '10px', textAlign: 'left' }}>
            <span style={{ fontSize: '0.85em', color: '#888', fontWeight: '500' }}>
              Source Language: 
              <div style={{ display: 'inline-block', position: 'relative', marginLeft: '8px' }} data-source-lang-dropdown>
                <input
                  id="source-language"
                  name="source-language"
                  type="text"
                  value={SUPPORTED_LANGUAGES[sourceLanguage]?.name || sourceLanguage}
                  readOnly
                  onClick={() => setSourceLanguageOpen(!sourceLanguageOpen)}
                  style={{
                    padding: '4px 20px 4px 8px',
                    fontSize: '0.85em',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    backgroundColor: 'white',
                    color: '#333',
                    cursor: 'pointer',
                    minWidth: '120px',
                    transition: 'all 0.2s ease',
                    boxShadow: sourceLanguageOpen ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
                    borderColor: sourceLanguageOpen ? '#007bff' : '#ddd'
                  }}
                />
                <div style={{
                  position: 'absolute',
                  right: '6px',
                  top: '50%',
                  transform: `translateY(-50%) rotate(${sourceLanguageOpen ? '180deg' : '0deg'})`,
                  transition: 'transform 0.2s ease',
                  pointerEvents: 'none',
                  fontSize: '0.7em',
                  color: '#666'
                }}>
                  ▼
                </div>
                {sourceLanguageOpen && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    border: '1px solid #007bff',
                    borderTop: 'none',
                    borderRadius: '0 0 6px 6px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    fontSize: '0.85em',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                  }}>
                    {Object.entries(SUPPORTED_LANGUAGES).map(([code, lang]) => {
                      if (code === 'auto' && !selectedAudioFile) return null;
                      const isSupported = speechService?.current?.isLanguageSupported(code) !== false;
                      return (
                        <div
                          key={code}
                          onClick={() => {
                            setSourceLanguage(code);
                            setSourceLanguageOpen(false);
                          }}
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            backgroundColor: sourceLanguage === code ? '#f0f8ff' : 'transparent',
                            borderBottom: '1px solid #f0f0f0'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = sourceLanguage === code ? '#f0f8ff' : 'transparent'}
                        >
                          {lang.flag} {lang.name} {!isSupported && code !== 'en' ? '(Limited Support)' : ''}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </span>
          </div>
          <div className="text-output">
            {transcribedText || (
              isRecording ? 'Listening... speak now!' : 
              isProcessingAudio && status.includes('Transcription in progress') ? (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '100px',
                  color: '#1976d2',
                  textAlign: 'center'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    border: '4px solid #e3f2fd',
                    borderTop: '4px solid #1976d2',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    marginBottom: '15px'
                  }}></div>
                  <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>
                    🎙️ Transcribing Audio...
                  </div>
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    Processing your audio file with speaker detection
                  </div>
                  <div style={{ fontSize: '12px', color: '#999', marginTop: '5px' }}>
                    This may take a few minutes depending on file length
                  </div>
                  <style>{`
                    @keyframes spin {
                      0% { transform: rotate(0deg); }
                      100% { transform: rotate(360deg); }
                    }
                  `}</style>
                </div>
              ) : 'No speech detected yet...'
            )}
          </div>
        </div>

        <div className="result-section">
          <h3 style={{ margin: '0 0 15px 0', color: '#333', fontSize: '1.1em' }}>Translated Text:</h3>
          {/* Target Language Selector */}
          <div style={{ marginBottom: '10px', textAlign: 'left' }}>
            <span style={{ fontSize: '0.85em', color: '#888', fontWeight: '500' }}>
              Target Language: 
              <div style={{ display: 'inline-block', position: 'relative', marginLeft: '8px' }} data-target-lang-dropdown>
                <input
                  id="target-language"
                  name="target-language"
                  type="text"
                  value={SUPPORTED_LANGUAGES[targetLanguage]?.name || targetLanguage}
                  readOnly
                  onClick={() => setTargetLanguageOpen(!targetLanguageOpen)}
                  style={{
                    padding: '4px 20px 4px 8px',
                    fontSize: '0.85em',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    backgroundColor: 'white',
                    color: '#333',
                    cursor: 'pointer',
                    minWidth: '120px',
                    transition: 'all 0.2s ease',
                    boxShadow: targetLanguageOpen ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
                    borderColor: targetLanguageOpen ? '#007bff' : '#ddd'
                  }}
                />
                <div style={{
                  position: 'absolute',
                  right: '6px',
                  top: '50%',
                  transform: `translateY(-50%) rotate(${targetLanguageOpen ? '180deg' : '0deg'})`,
                  transition: 'transform 0.2s ease',
                  pointerEvents: 'none',
                  fontSize: '0.7em',
                  color: '#666'
                }}>
                  ▼
                </div>
                {targetLanguageOpen && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    border: '1px solid #007bff',
                    borderTop: 'none',
                    borderRadius: '0 0 6px 6px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    fontSize: '0.85em',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                  }}>
                    {Object.entries(SUPPORTED_LANGUAGES).map(([code, lang]) => {
                      if (code === 'auto') return null;
                      return (
                        <div
                          key={code}
                          onClick={() => {
                            setTargetLanguage(code);
                            setTargetLanguageOpen(false);
                          }}
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            backgroundColor: targetLanguage === code ? '#f0f8ff' : 'transparent',
                            borderBottom: '1px solid #f0f0f0'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = targetLanguage === code ? '#f0f8ff' : 'transparent'}
                        >
                          {lang.flag} {lang.name}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </span>
          </div>

          {/* Show Reasoning Toggle - Only for OpenAI models */}
          {BEDROCK_MODELS[selectedModel]?.provider === 'OpenAI' && (
            <div style={{ marginBottom: '15px' }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px', 
                fontSize: '14px',
                color: '#666', 
                fontWeight: '500',
                cursor: 'pointer'
              }}>
                <input
                  id="show-reasoning"
                  name="show-reasoning"
                  type="checkbox"
                  checked={showReasoning}
                  onChange={(e) => setShowReasoning(e.target.checked)}
                />
                Show Model Reasoning ({showReasoning 
                  ? '🧠 Shows thinking process, uses more tokens' 
                  : '🎯 Hides reasoning, cleaner output'
                })
              </label>
            </div>
          )}

          <div className="text-output">
            {translatedText || 'Translation will appear here...'}
          </div>
        </div>
      </div>

      {/* Two-column layout for History and Token Usage */}
      <div className="grid-container" style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
        gap: '30px', 
        marginTop: '30px',
        marginBottom: '30px'
      }}>
        {/* Left column - Token Usage */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          border: '1px solid #e9ecef',
          textAlign: 'left'
        }}>
          <Suspense fallback={<div>Loading...</div>}>
            {tokenUsage ? (
              <TokenUsageSection tokenUsage={tokenUsage} />
            ) : (
              <div style={{ color: '#666', fontSize: '14px', textAlign: 'center', padding: '20px' }}>
                <p>Token usage and cost information will appear here after translations.</p>
                <p style={{ fontSize: '12px', opacity: 0.7 }}>Track your API usage and costs in real-time</p>
              </div>
            )}
          </Suspense>
        </div>

        {/* Right column - Translation History */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          border: '1px solid #e9ecef',
          textAlign: 'left'
        }}>
          <h3 style={{ margin: '0 0 15px 0', color: '#333', fontSize: '1.1em' }}>📝 Translation History</h3>
          <Suspense fallback={<div>Loading...</div>}>
            {translationHistory.length > 0 ? (
              <TranslationHistory 
                history={translationHistory} 
                onClear={() => setTranslationHistory([])}
                supportedLanguages={SUPPORTED_LANGUAGES}
              />
            ) : (
              <div style={{ color: '#666', fontSize: '14px', textAlign: 'center', padding: '20px' }}>
                <p>Your translation history will appear here.</p>
                <p style={{ fontSize: '12px', opacity: 0.7 }}>Start translating to see your history</p>
              </div>
            )}
          </Suspense>
        </div>
      </div>

      {/* Floating Status Notification */}
      <StatusDisplay status={status} isRecording={isRecording} />
    </div>
  );
}

function App() {
  return (
    <AuthWrapper>
      <AppContent />
    </AuthWrapper>
  );
}

export default App;
